import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { coQuyenModuleDaCai } from '../../../hooks/usePermission';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { useToast } from '../../../lib/toast';
import { boKhoiTaoRepository } from '../../../lib/repositories/bo-khoi-tao.repository';
import { moduleToChucRepository } from '../../../lib/repositories/module-to-chuc.repository';
import type { ApiError } from '../../../lib/errors';
import { useAuthStore } from '../../../stores/auth';
import type { ModulePackageResult, ModuleVersion } from '../../quan-ly-module/types';
import { quyenManModule } from '../nghiep-vu/quyen';
import type { QuyenManModule } from '../nghiep-vu/quyen';
import { conTheoDoi } from '../nghiep-vu/trang-thai-cai';
import type { DanhMucBoKhoiTao, LuotCai, ModuleDaDuyet } from '../types';

const KHOA = ['module-to-chuc'] as const;
/** Danh sách module đã cài (`useModuleDaCai`), nguồn của các nhóm module trên thanh điều hướng. */
const KHOA_DA_CAI = ['module-da-cai'] as const;
/** Danh mục quyền gán được của màn Vai trò (`useDanhMucQuyen`): module vừa cài thêm một nhóm quyền. */
const KHOA_DANH_MUC_QUYEN = ['permissions'] as const;

function thuLai(soLanDaThu: number, err: unknown): boolean {
  const status = (err as ApiError | undefined)?.status;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return soLanDaThu < 1;
}

/**
 * Quyền `core:module:*` của người đang đăng nhập (ADR-0104). Quản trị tổ chức có sẵn cả ba, nên không phải chờ
 * `me/context` nạp lại; người khác theo đúng mã được gán qua vai trò.
 */
export function useQuyenManModule(): QuyenManModule {
  const user = useAuthStore((s) => s.user);
  return quyenManModule((ma) => coQuyenModuleDaCai(user, ma));
}

/** `bat = false` khi người dùng không có quyền xem: không gọi API chỉ để nhận 403. */
export function useModuleDaDuyet(bat = true) {
  return useQuery<ModuleDaDuyet[]>({
    queryKey: [...KHOA, 'da-duyet'],
    queryFn: moduleToChucRepository.daDuyet,
    enabled: bat,
    staleTime: 30_000,
    retry: thuLai,
  });
}

/** Danh mục bộ khởi tạo (ADR-0113). `bat = false` khi người dùng không có quyền nộp gói: bộ này dành cho người viết module. */
export function useBoKhoiTao(bat = true) {
  return useQuery<DanhMucBoKhoiTao>({
    queryKey: [...KHOA, 'bo-khoi-tao'],
    queryFn: boKhoiTaoRepository.danhMuc,
    enabled: bat,
    // Tệp tĩnh chỉ đổi khi triển khai bản mới
    staleTime: 5 * 60_000,
    retry: thuLai,
  });
}

/** `bat = false` khi người dùng không có quyền nộp gói. */
export function useGoiCuaToi(bat = true) {
  return useQuery<ModuleVersion[]>({
    queryKey: [...KHOA, 'goi-cua-toi'],
    queryFn: moduleToChucRepository.goiCuaToi,
    enabled: bat,
    staleTime: 30_000,
    retry: thuLai,
  });
}

/**
 * Mọi phiên bản của module riêng tổ chức, mọi người nộp (ADR-0115): hàng chờ duyệt và lịch sử cập nhật. Chỉ quản trị
 * tổ chức gọi được; `bat = false` khi người dùng không phải quản trị tổ chức.
 */
export function useChoDuyetToChuc(bat = true) {
  return useQuery<ModuleVersion[]>({
    queryKey: [...KHOA, 'cho-duyet'],
    queryFn: moduleToChucRepository.choDuyet,
    enabled: bat,
    staleTime: 15_000,
    retry: thuLai,
  });
}

/** Duyệt hay từ chối làm đổi Marketplace của tổ chức và module đã cài (khi duyệt bản mới): làm mới cả nhóm. */
function useLamMoiSauDuyet() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: KHOA });
    void queryClient.invalidateQueries({ queryKey: KHOA_DA_CAI });
  };
}

export function useDuyetModuleToChuc() {
  const lamMoi = useLamMoiSauDuyet();
  const toast = useToast();
  const { t } = useT();
  const dichLoi = useLoi();
  return useMutation<ModuleVersion, unknown, { code: string; version: string }>({
    mutationFn: (bien) => moduleToChucRepository.duyet(bien.code, bien.version),
    onSuccess: (v) => {
      lamMoi();
      toast.success(t('moduleToChuc.choDuyet.daDuyet', { ten: v.name, phienBan: v.version }));
    },
    onError: (err) => toast.error(dichLoi(err)),
  });
}

export function useTuChoiModuleToChuc() {
  const lamMoi = useLamMoiSauDuyet();
  const toast = useToast();
  const { t } = useT();
  return useMutation<ModuleVersion, unknown, { code: string; version: string; reason: string }>({
    mutationFn: (bien) => moduleToChucRepository.tuChoi(bien.code, bien.version, bien.reason),
    onSuccess: (v) => {
      lamMoi();
      toast.success(t('moduleToChuc.choDuyet.daTuChoi', { ten: v.name, phienBan: v.version }));
    },
    // Lỗi không toast: hộp từ chối giữ lý do vừa nhập và hiện lỗi ngay trong hộp.
  });
}

/** Nộp gói ở cổng tenant. Một khoá lập cho mỗi lần mở hộp, như `useNopGoiModule` của cổng nền tảng. */
export function useNopGoiToChuc() {
  const queryClient = useQueryClient();
  const khoaLapRef = useRef<string>(crypto.randomUUID());
  return useMutation<ModulePackageResult, unknown, File>({
    mutationFn: (tep: File) => moduleToChucRepository.nopGoi(tep, khoaLapRef.current),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KHOA });
      khoaLapRef.current = crypto.randomUUID();
    },
  });
}

const khoaLuotCai = (code: string) => [...KHOA, 'luot-cai', code] as const;

/**
 * Lượt mới server vừa nhận ghi thẳng vào cache của `useLuotCai`: cache còn giữ trạng thái cuối của lượt trước (ACTIVE,
 * REMOVED) thì `refetchInterval` đã tắt, và màn không bao giờ thấy lượt mới chạy.
 */
function useGhiLuotCai() {
  const queryClient = useQueryClient();
  return (luot: LuotCai) => queryClient.setQueryData(khoaLuotCai(luot.code), luot);
}

/** `POST …/install`; lỗi trả về nơi gọi để hiện ngay cạnh dòng module đó. */
export function useCaiModule() {
  const ghi = useGhiLuotCai();
  return useMutation<LuotCai, unknown, string>({
    mutationFn: (code: string) => moduleToChucRepository.cai(code, crypto.randomUUID()),
    onSuccess: ghi,
  });
}

/**
 * Người nộp xoá hẳn module khỏi Marketplace (ADR-0112): làm mới cả hai bảng của màn, menu module đã cài và danh mục
 * quyền, vì module biến khỏi mọi chỗ cùng lúc.
 */
export function useXoaModuleCuaToi() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: (code: string) => moduleToChucRepository.xoaModule(code),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KHOA });
      void queryClient.invalidateQueries({ queryKey: KHOA_DA_CAI });
      void queryClient.invalidateQueries({ queryKey: KHOA_DANH_MUC_QUYEN });
    },
  });
}

/** `POST …/uninstall`, giữ dữ liệu của module (ADR-0049 mục 3). */
export function useGoModule() {
  const ghi = useGhiLuotCai();
  return useMutation<LuotCai, unknown, string>({
    mutationFn: (code: string) => moduleToChucRepository.go(code, crypto.randomUUID()),
    onSuccess: ghi,
  });
}

/**
 * Lượt cài của một module, hỏi lại mỗi 2 giây khi workflow cài hay gỡ còn chạy (tới 120 giây, ADR-0048). Lên ACTIVE
 * hay REMOVED thì làm mới danh sách module đã cài để nhóm của module hiện ra hay biến khỏi thanh điều hướng ngay, và
 * danh mục quyền để màn Vai trò có hay mất nhóm quyền của module.
 */
export function useLuotCai(code: string, bat: boolean) {
  const queryClient = useQueryClient();
  return useQuery<LuotCai>({
    queryKey: khoaLuotCai(code),
    queryFn: async () => {
      const luot = await moduleToChucRepository.luotCai(code);
      if (luot.status === 'ACTIVE' || luot.status === 'REMOVED') {
        void queryClient.invalidateQueries({ queryKey: KHOA_DA_CAI });
        void queryClient.invalidateQueries({ queryKey: KHOA_DANH_MUC_QUYEN });
      }
      return luot;
    },
    enabled: bat,
    retry: thuLai,
    refetchInterval: (q) => (conTheoDoi(q.state.data?.status) ? 2_000 : false),
  });
}
